import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Accordion, Card, Button, Container, Row, Col, Spinner, Table, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaSearch, FaCode, FaInfoCircle } from 'react-icons/fa';
import { StatusCandidato } from '../../model/Candidato/Candidato';
import { publicApi } from '../../api';

export interface CandidatoSimpleResponse {
    inscricao: string;
    nome: string;
    nomeSocial: string | null;
    status: StatusCandidato;
    editalProcesso: string;
    identificadorProcesso: string;
}

export interface CandidatoSearchResult extends CandidatoSimpleResponse {
    nomeExibicao: string;
}

export interface CandidatoSearchResponse {
    candidatos: CandidatoSearchResult[];
    totalCandidatos: number;
}

const CandidateSearchPage = () => {
    const [searchResults, setSearchResults] = useState<CandidatoSearchResult[]>([]);
    const [loading, setLoading] = useState(false);
    const [apiExample, setApiExample] = useState('');
    const [showApiDocs, setShowApiDocs] = useState(false);

    const validationSchema = Yup.object().shape({
        cpf: Yup.string(),
        passaporte: Yup.string(),
        cpfResponsavel: Yup.string(),
        passaporteResponsavel: Yup.string(),
    }).test({
        name: 'at-least-one-required',
        message: 'Pelo menos um documento deve ser fornecido',
        test: (values) => {
            return !!(
                values.cpf ||
                values.passaporte ||
                values.cpfResponsavel ||
                values.passaporteResponsavel
            );
        },
    });

    const handleSubmit = async (values: any) => {
        setLoading(true);
        try {
            const params: Record<string, string> = {};
            Object.entries(values).forEach(([key, value]) => {
                if (value && typeof value === 'string' && value.trim() !== '') {
                    params[key] = value;
                }
            });

            const response = await publicApi<CandidatoSearchResponse>('/historico/candidatos/busca', { params });

            const results = response.data.candidatos.map(candidato => ({
                ...candidato,
                nomeExibicao: candidato.nomeSocial || candidato.nome
            }));

            setSearchResults(results);
            setApiExample(`GET https://api.unicamp.br/identifica/v1/historico/candidatos/busca?${Object.keys(params).map(key => `${key}=${params[key]}`).join('&')}`);

            if (results.length === 0) {
                toast.info('Nenhum candidato encontrado com os critérios fornecidos');
            } else {
                toast.success(`${results.length} candidato(s) encontrado(s)!`);
            }
        } catch (error: any) {
            setSearchResults([]);

            // Tratamento específico para o caso de "Candidato não encontrado" (400 ou 404)
            if (axios.isAxiosError(error) &&
                (error.response?.status === 400 || error.response?.status === 404) &&
                Array.isArray(error.response.data)) {
                const errorMessage = error.response.data[0]?.message;
                if (errorMessage === "Candidato não encontrado") {
                    toast.info(errorMessage); // Mostra como informação, não erro
                    return;
                }
            }

            // Tratamento genérico para outros erros
            let errorMessage = "Erro ao buscar candidato(s)";
            if (axios.isAxiosError(error)) {
                if (error.response) {
                    errorMessage = error.response.data?.message ||
                        (Array.isArray(error.response.data) ? error.response.data[0]?.message : null) ||
                        `Erro ${error.response.status}: ${error.response.statusText}`;
                } else {
                    errorMessage = error.message || "Erro de conexão com o servidor";
                }
            } else if (error.message) {
                errorMessage = error.message;
            }

            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text)
            .then(() => toast.info('Copiado para a área de transferência!'))
            .catch(() => toast.error('Falha ao copiar texto'));
    };

    const sortedResults = [...searchResults].sort((a: any, b: any) => {
        if (a.status === 'VALIDADO' && b.status !== 'VALIDADO') return -1;
        if (a.status !== 'VALIDADO' && b.status === 'VALIDADO') return 1;
        return 0;
    });

    return (
        <Container className="mt-4">
            <Row className="mb-4">
                <Col>
                    <h2>Histórico de Heteroidentificação de Candidato</h2>
                    <p className="text-muted">Preencha pelo menos um dos campos abaixo para buscar um candidato com seu histórico (em processos finalizados)</p>
                </Col>
                <Col xs="auto">
                    <Button
                        variant="outline-secondary"
                        onClick={() => setShowApiDocs(!showApiDocs)}
                        size="sm"
                    >
                        <FaCode className="me-2" /> {showApiDocs ? 'Ocultar' : 'Mostrar'} API Docs
                    </Button>
                </Col>
            </Row>

            <Row>
                <Col md={showApiDocs ? 8 : 12}>
                    <Card className="mb-4">
                        <Card.Body>
                            <Card.Title>
                                <FaSearch className="me-2" /> Buscar Candidato
                            </Card.Title>
                            <Formik
                                initialValues={{
                                    cpf: '',
                                    passaporte: '',
                                    cpfResponsavel: '',
                                    passaporteResponsavel: ''
                                }}
                                validationSchema={validationSchema}
                                onSubmit={handleSubmit}
                            >
                                {({ isSubmitting }) => (
                                    <Form>
                                        <Row>
                                            <Col md={6}>
                                                <div className="mb-3">
                                                    <label htmlFor="cpf" className="form-label">CPF do Candidato</label>
                                                    <Field
                                                        type="text"
                                                        name="cpf"
                                                        className="form-control"
                                                        placeholder="Somente números"
                                                    />
                                                    <small className="text-muted">Sem pontos ou traços</small>
                                                </div>

                                                <div className="mb-3">
                                                    <label htmlFor="passaporte" className="form-label">Passaporte do Candidato</label>
                                                    <Field
                                                        type="text"
                                                        name="passaporte"
                                                        className="form-control"
                                                        placeholder="Número do passaporte"
                                                    />
                                                </div>
                                            </Col>
                                            <Col md={6}>
                                                <div className="mb-3">
                                                    <label htmlFor="cpfResponsavel" className="form-label">CPF do Responsável</label>
                                                    <Field
                                                        type="text"
                                                        name="cpfResponsavel"
                                                        className="form-control"
                                                        placeholder="Somente números"
                                                    />
                                                    <small className="text-muted">Para candidatos menores</small>
                                                </div>

                                                <div className="mb-3">
                                                    <label htmlFor="passaporteResponsavel" className="form-label">Passaporte do Responsável</label>
                                                    <Field
                                                        type="text"
                                                        name="passaporteResponsavel"
                                                        className="form-control"
                                                        placeholder="Número do passaporte"
                                                    />
                                                </div>
                                            </Col>
                                        </Row>

                                        <ErrorMessage
                                            name="at-least-one-required"
                                            component="div"
                                            className="text-danger mb-3"
                                        />

                                        <div className="d-grid gap-2">
                                            <Button
                                                variant="primary"
                                                type="submit"
                                                disabled={loading}
                                                size="lg"
                                            >
                                                {loading ? (
                                                    <>
                                                        <Spinner animation="border" size="sm" /> Buscando...
                                                    </>
                                                ) : (
                                                    <>
                                                        <FaSearch className="me-2" /> Buscar Candidato
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </Form>
                                )}
                            </Formik>
                        </Card.Body>
                    </Card>

                    {searchResults.length > 0 && (
                        <Card>
                            <Card.Body>
                                <Card.Title>Informações dos Candidatos</Card.Title>
                                <Table striped bordered responsive>
                                    <thead>
                                        <tr>
                                            <th>Inscrição</th>
                                            <th>Nome</th>
                                            <th>Status</th>
                                            <th>Edital</th>
                                            <th>Processo</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedResults.map((candidato, index) => (
                                            <tr key={index}>
                                                <td>{candidato.inscricao}</td>
                                                <td>{candidato.nomeExibicao}</td>
                                                <td>
                                                    <span className={`badge bg-${getStatusBadgeColor(candidato.status)}`}>
                                                        {formatStatusText(candidato.status)}
                                                    </span>
                                                </td>
                                                <td>{candidato.editalProcesso}</td>
                                                <td>{candidato.identificadorProcesso}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    )}
                </Col>

                {showApiDocs && (
                    <Col md={4}>
                        <Card>
                            <Card.Body>
                                <Card.Title>
                                    <FaCode className="me-2" /> Documentação da API
                                </Card.Title>

                                <Accordion defaultActiveKey="0">
                                    <Accordion.Item eventKey="0">
                                        <Accordion.Header>Endpoint GET https://api.unicamp.br/identifica/v1/historico/candidatos/busca</Accordion.Header>
                                        <Accordion.Body>
                                            <p>Busca candidatos por documentos próprios ou de seus responsáveis.</p>
                                            <p>
                                                (Apenas para Unidades da Unicamp cadastradas e permitidas no Identifica)
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                        <Tooltip id="tooltip-permissao">
                                                            Para cadastrar e obter permissão, contactar a DEDH.
                                                        </Tooltip>
                                                    }
                                                >
                                                    <span className="ms-2" style={{ cursor: 'pointer' }}>
                                                        <FaInfoCircle />
                                                    </span>
                                                </OverlayTrigger>
                                            </p>

                                            <h5>Cabeçalhos Obrigatórios:</h5>
                                            <pre className="p-2 bg-light rounded mb-3">
                                                {`Authorization: Bearer <token_sensedia>
AuthorizationAC: Bearer <token_autenticacao_central>`}
                                            </pre>

                                            <h5>Parâmetros:</h5>
                                            <ul>
                                                <li><code>Token Sensedia</code> - Obtido por fluxo OAuth com Sensedia, com credenciais de App Consumidor</li>
                                                <li><code>Token Autenticação Central</code> - Obtido com credenciais de Login na Autenticação Central</li>
                                                <li><code>cpf</code> - CPF do candidato (opcional)</li>
                                                <li><code>passaporte</code> - Passaporte do candidato (opcional)</li>
                                                <li><code>cpfResponsavel</code> - CPF do responsável (opcional)</li>
                                                <li><code>passaporteResponsavel</code> - Passaporte do responsável (opcional)</li>
                                            </ul>

                                            <h5>Exemplo de Requisição Completa:</h5>
                                            <pre className="p-2 bg-light rounded mb-3">
                                                {`GET /identifica/v1/historico/candidatos/busca?cpf=12345678901 HTTP/1.1
Host: api.unicamp.br
Authorization: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...
AuthorizationAC: Bearer eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9...`}
                                            </pre>

                                            <h5 className="mt-3">Resposta:</h5>
                                            <pre className="p-2 bg-light rounded">
                                                {`HTTP/1.1 200 OK
Content-Type: application/json

{
  "candidatos": [
    {
      "inscricao": "20230001",
      "nome": "Fulano da Silva",
      "nomeSocial": "Fulana",
      "status": "VALIDADO",
      "editalProcesso": "Processo Seletivo 2023",
      "identificadorProcesso": "PS2023",
      "nomeExibicao": "Fulana Silva (20230001)"
    }
  ],
  "totalCandidatos": 1
}`}
                                            </pre>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                </Accordion>
                            </Card.Body>
                        </Card>
                    </Col>
                )}
            </Row>
        </Container>
    );
};

// Helper para cores dos status
const getStatusBadgeColor = (status: string) => {
    switch (status) {
        case "VALIDADO": return 'success';
        case "NAO_VALIDADO": return 'danger';
        case "NAO_AVALIADO": return 'warning';
        case "AUSENTE": return 'secondary';
        case "DELETADO": return 'dark';
        default: return 'primary';
    }
};

// Helper para formatar texto do status
const formatStatusText = (status: string) => {
    switch (status) {
        case "VALIDADO": return 'Validado';
        case "NAO_VALIDADO": return 'Não Validado';
        case "NAO_AVALIADO": return 'Não avaliado';
        case "AUSENTE": return 'Ausente';
        case "DELETADO": return 'Deletado';
        default: return status;
    }
};

export default CandidateSearchPage;